import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, TrendingUp, Info, DollarSign } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'takehome_salary_data';

export const TakeHomeSalaryCalculator = () => {
  const [ctc, setCtc] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).ctc : 1200000;
  });
  
  const [taxRegime, setTaxRegime] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).taxRegime : 'new';
  });
  
  const [epfContribution, setEpfContribution] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).epfContribution : 1800;
  });
  
  const [professionalTax, setProfessionalTax] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).professionalTax : 200;
  });
  
  const [otherDeductions, setOtherDeductions] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).otherDeductions : 1000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ctc,
      taxRegime,
      epfContribution,
      professionalTax,
      otherDeductions
    }));
  }, [ctc, taxRegime, epfContribution, professionalTax, otherDeductions]);

  const calculateTax = (annualIncome, regime) => {
    if (regime === 'new') {
      // New Tax Regime (FY 2024-25)
      if (annualIncome <= 300000) return 0;
      else if (annualIncome <= 700000) return (annualIncome - 300000) * 0.05;
      else if (annualIncome <= 1000000) return 20000 + (annualIncome - 700000) * 0.10;
      else if (annualIncome <= 1200000) return 50000 + (annualIncome - 1000000) * 0.15;
      else if (annualIncome <= 1500000) return 80000 + (annualIncome - 1200000) * 0.20;
      else return 140000 + (annualIncome - 1500000) * 0.30;
    } else {
      // Old Tax Regime
      const standardDeduction = 50000;
      const taxableIncome = annualIncome - standardDeduction;
      
      if (taxableIncome <= 250000) return 0;
      else if (taxableIncome <= 500000) return (taxableIncome - 250000) * 0.05;
      else if (taxableIncome <= 1000000) return 12500 + (taxableIncome - 500000) * 0.20;
      else return 112500 + (taxableIncome - 1000000) * 0.30;
    }
  };

  const calculate = () => {
    const annualCtc = parseFloat(ctc) || 0;
    const epf = (parseFloat(epfContribution) || 0) * 12;
    const profTax = (parseFloat(professionalTax) || 0) * 12;
    const otherDed = (parseFloat(otherDeductions) || 0) * 12;

    // Gross salary (CTC - employer contributions)
    const employerPF = epf; // Assuming employer matches employee PF
    const grossSalary = annualCtc - employerPF;

    // Taxable income
    const taxableIncome = grossSalary - epf;
    
    // Calculate tax
    const incomeTax = calculateTax(taxableIncome, taxRegime);
    const cess = incomeTax * 0.04; // 4% health and education cess
    const totalTax = incomeTax + cess;

    // Total deductions
    const totalDeductions = epf + profTax + otherDed + totalTax;

    // Take-home
    const annualTakeHome = grossSalary - totalDeductions;
    const monthlyTakeHome = annualTakeHome / 12;

    // Monthly gross
    const monthlyGross = grossSalary / 12;

    return {
      grossSalary,
      monthlyGross,
      totalTax,
      epf,
      profTax,
      otherDed,
      totalDeductions,
      annualTakeHome,
      monthlyTakeHome,
      effectiveTaxRate: grossSalary > 0 ? (totalTax / grossSalary) * 100 : 0,
      takeHomePercent: grossSalary > 0 ? (annualTakeHome / grossSalary) * 100 : 0
    };
  };

  const generateDeductionBreakdown = () => {
    const results = calculate();
    
    return [
      { name: 'EPF', value: results.epf },
      { name: 'Income Tax', value: results.totalTax },
      { name: 'Professional Tax', value: results.profTax },
      { name: 'Other Deductions', value: results.otherDed }
    ];
  };

  const generateCtcVsTakeHome = () => {
    const results = calculate();
    const annualCtc = parseFloat(ctc) || 0;
    
    return [
      { category: 'CTC', amount: annualCtc },
      { category: 'Gross Salary', amount: results.grossSalary },
      { category: 'Total Deductions', amount: results.totalDeductions },
      { category: 'Take-Home', amount: results.annualTakeHome }
    ];
  };

  const generateRegimeComparison = () => {
    const annualCtc = parseFloat(ctc) || 0;
    const epf = (parseFloat(epfContribution) || 0) * 12;
    const employerPF = epf;
    const grossSalary = annualCtc - employerPF;
    const taxableIncome = grossSalary - epf;

    const oldRegimeTax = calculateTax(taxableIncome, 'old');
    const newRegimeTax = calculateTax(taxableIncome, 'new');

    const oldCess = oldRegimeTax * 0.04;
    const newCess = newRegimeTax * 0.04;

    return [
      { regime: 'Old Regime', tax: oldRegimeTax + oldCess },
      { regime: 'New Regime', tax: newRegimeTax + newCess }
    ];
  };

  const results = calculate();
  const deductionData = generateDeductionBreakdown();
  const ctcComparisonData = generateCtcVsTakeHome();
  const regimeComparisonData = generateRegimeComparison();

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Take-Home Salary Calculator
            </h1>
          </div>
          <p className="text-muted-foreground">Calculate your actual in-hand salary after all deductions</p>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Take-Home Salary?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Take-home salary is the actual amount you receive in your bank account after all deductions like EPF, tax, 
                  and other statutory payments. Understanding this helps in better budgeting and financial planning.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your salary details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="ctc" className="text-sm font-medium">
                      CTC (Annual) ($)
                    </Label>
                    <Input
                      id="ctc"
                      type="number"
                      value={ctc}
                      onChange={(e) => setCtc(e.target.value)}
                      placeholder="1200000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Cost to Company per year</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxRegime" className="text-sm font-medium">
                      Tax Regime
                    </Label>
                    <Select value={taxRegime} onValueChange={setTaxRegime}>
                      <SelectTrigger id="taxRegime">
                        <SelectValue placeholder="Select regime" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="old">Old Regime (with deductions)</SelectItem>
                        <SelectItem value="new">New Regime (lower rates)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Choose your preferred tax regime</p>
                  </div>

                  <Separator />
                  <p className="text-sm font-semibold text-foreground">Monthly Deductions</p>

                  <div className="space-y-2">
                    <Label htmlFor="epfContribution" className="text-sm font-medium">
                      EPF Contribution ($)
                    </Label>
                    <Input
                      id="epfContribution"
                      type="number"
                      value={epfContribution}
                      onChange={(e) => setEpfContribution(e.target.value)}
                      placeholder="1800"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Usually 12% of basic</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="professionalTax" className="text-sm font-medium">
                      Professional Tax ($)
                    </Label>
                    <Input
                      id="professionalTax"
                      type="number"
                      value={professionalTax}
                      onChange={(e) => setProfessionalTax(e.target.value)}
                      placeholder="200"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Varies by state</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="otherDeductions" className="text-sm font-medium">
                      Other Deductions ($)
                    </Label>
                    <Input
                      id="otherDeductions"
                      type="number"
                      value={otherDeductions}
                      onChange={(e) => setOtherDeductions(e.target.value)}
                      placeholder="1000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Insurance, loans, etc.</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setCtc(ctc)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Take-Home Salary Results</CardTitle>
                    <CardDescription>Your actual earnings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly Take-Home</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.monthlyTakeHome)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.takeHomePercent.toFixed(1)}% of gross</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Annual Take-Home</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.annualTakeHome)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly Gross Salary</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.monthlyGross)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Deductions (Annual)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.totalDeductions)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Income Tax (with cess)</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.totalTax)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {results.effectiveTaxRate.toFixed(2)}% effective tax rate
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">EPF Contribution (Annual)</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {formatCurrency(results.epf)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Building retirement corpus
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">CTC vs Take-Home Breakdown</CardTitle>
                <CardDescription className="text-base">
                  Understand where your CTC goes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ctcComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="category" 
                        stroke="hsl(var(--muted-foreground))"
                        angle={-15}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="amount" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Deduction Breakdown</CardTitle>
                  <CardDescription>
                    Where your deductions go
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deductionData}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {deductionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Tax Regime Comparison</CardTitle>
                  <CardDescription>
                    Old vs New regime impact
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={regimeComparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="regime" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="tax" fill="hsl(var(--secondary))" name="Annual Tax" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Take-Home Salary</CardTitle>
                <CardDescription className="text-base">
                  Learn why take-home is lower than CTC and how to optimize it
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Why Take-Home is Lower than CTC
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your CTC is <strong className="text-primary">{formatCurrency(parseFloat(ctc))}</strong> but your 
                    take-home is only <strong className="text-primary">{formatCurrency(results.annualTakeHome)}</strong>. 
                    That's a difference of <strong>{formatCurrency(parseFloat(ctc) - results.annualTakeHome)}</strong>!
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="mb-2">The gap exists because CTC includes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Employer's PF contribution (not in your account)</li>
                      <li>Income tax deductions (TDS)</li>
                      <li>Employee PF contribution (retirement savings)</li>
                      <li>Professional tax and other statutory deductions</li>
                    </ul>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Tax Regime Comparison
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    You're currently on the <strong className="text-secondary">{taxRegime === 'old' ? 'Old' : 'New'}</strong> tax regime, 
                    paying <strong>{formatCurrency(results.totalTax)}</strong> in annual tax.
                  </p>
                  
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">Old Regime</h4>
                      <p className="text-muted-foreground">
                        Higher tax rates but allows deductions under 80C, 80D, HRA, etc. Better if you have 
                        significant investments in tax-saving instruments.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">New Regime</h4>
                      <p className="text-muted-foreground">
                        Lower tax rates but no deductions allowed (except standard deduction of $50,000). 
                        Better for those who don't invest much in tax-saving options.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Tax-Saving Improvement Ideas
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">💰 Maximize 80C (Old Regime)</h4>
                      <p className="text-muted-foreground">
                        Invest up to $1.5L in ELSS, PPF, or life insurance to save up to $46,800 in tax.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">💰 Health Insurance (80D)</h4>
                      <p className="text-muted-foreground">
                        Health insurance premiums ($25K for self + $25K for parents) can save additional tax in old regime.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">💰 HRA Exemption (Old Regime)</h4>
                      <p className="text-muted-foreground">
                        If paying rent, claim HRA exemption with rent receipts. Can save significant tax based on rent paid.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">💰 NPS Additional Benefit (80CCD(1B))</h4>
                      <p className="text-muted-foreground">
                        Extra $50K deduction available for NPS contributions. Can save up to $15,600 more in old regime.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Salary Insights
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Employer PF is Hidden:</strong> Your employer contributes 
                        12% of basic to EPF, which is part of CTC but doesn't come to your account directly.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Effective Tax Rate:</strong> Your effective tax rate is 
                        {results.effectiveTaxRate.toFixed(2)}%, which is lower than marginal rate due to slabs.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Variable Components:</strong> Bonuses, stock options, and 
                        one-time benefits are often part of CTC but not regular monthly income.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Professional Tax Varies:</strong> Professional tax is 
                        state-specific. Some states charge $200/month, others up to $2,500/year.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Salary Restructuring Tips
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Flexible Benefits Plan</h4>
                      <p className="text-muted-foreground">
                        Many companies offer flexible benefits (food coupons, fuel, books) which are tax-free up to limits. 
                        Use them to reduce taxable income.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Optimize Basic vs HRA</h4>
                      <p className="text-muted-foreground">
                        Keep basic at 40-50% of gross. Higher basic means higher PF but less HRA exemption benefit.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Choose Right Regime Annually</h4>
                      <p className="text-muted-foreground">
                        Review and choose the optimal tax regime each year based on your investments and salary structure.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TakeHomeSalaryCalculator;
